/**
 * 路由守卫。
 *
 * 关键在于**三态**，不是两态：`checking` 与「确定没登录」必须分开。
 * 只判 `user ? 页面 : 跳登录` 的话，刷新页面时 `/auth/status` 还没回来，
 * 守卫先看到 user===null，把已登录的用户弹去登录页，然后 status 回来再弹回来——
 * 一次刷新闪两下，且中途丢掉原路径。
 */

import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { Skeleton } from 'antd';
import { Navigate, useLocation } from 'react-router-dom';
import { hasAccess, useAuthStore } from '@/store/auth';

export function RequireAuth({ children }: { children: ReactNode }) {
  const location = useLocation();
  const phase = useAuthStore((s) => s.phase);
  const authRequired = useAuthStore((s) => s.authRequired);
  const user = useAuthStore((s) => s.user);
  const bootstrap = useAuthStore((s) => s.bootstrap);

  useEffect(() => {
    if (phase === 'idle') void bootstrap();
  }, [phase, bootstrap]);

  if (phase !== 'ready') {
    return <Skeleton active style={{ padding: 24 }} />;
  }

  if (!hasAccess({ authRequired, user })) {
    // 把当前路径带上：登录后回到用户本来想去的地方，而不是一律回总览。
    // 用 state 而不是 query，免得原路径里的 ? 与 # 还要再编码一层。
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}
